export const LIVE_STUDIO_SETTINGS_VERSION = 4;
export const LIVE_STUDIO_SETTINGS_KEY = 'radiosuitex.liveStudio.settings.v4';

export const LIVE_STUDIO_WIDGET_IDS = Object.freeze([
  'participants',
  'mixer',
  'playout',
  'side',
]);

const LEGACY_SETTINGS_KEYS = [
  'radiosuitex.liveStudio.settings.v3',
  'radiosuitex.liveStudio.settings.v2',
  'radiosuitex.liveStudio.settings.v1',
];

const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

export const createDefaultLiveStudioLayout = () => ({
  lg: [
    { i: 'participants', x: 0, y: 0, w: 7, h: 6, minW: 3, minH: 4 },
    { i: 'mixer', x: 7, y: 0, w: 5, h: 6, minW: 3, minH: 4 },
    { i: 'playout', x: 0, y: 6, w: 9, h: 12, minW: 4, minH: 6 },
    { i: 'side', x: 9, y: 6, w: 3, h: 12, minW: 2, minH: 4 },
  ],
  xs: [
    { i: 'participants', x: 0, y: 0, w: 1, h: 5, minW: 1, minH: 3 },
    { i: 'mixer', x: 0, y: 5, w: 1, h: 5, minW: 1, minH: 4 },
    { i: 'playout', x: 0, y: 10, w: 1, h: 12, minW: 1, minH: 6 },
    { i: 'side', x: 0, y: 22, w: 1, h: 6, minW: 1, minH: 3 },
  ],
});

export const createDefaultLiveStudioSettings = () => ({
  version: LIVE_STUDIO_SETTINGS_VERSION,
  stationId: '',
  devices: {
    audioInputId: '',
    audioOutputId: '',
    videoInputId: '',
  },
  defaultFaders: {
    program: 0.85,
    host: 0.8,
    guest: 0.75,
  },
  widgets: [...LIVE_STUDIO_WIDGET_IDS],
  layout: createDefaultLiveStudioLayout(),
});

const normalizeDeviceId = (value) => (typeof value === 'string' ? value : '');

const normalizeLayoutItem = (item, fallback) => {
  if (!item || typeof item !== 'object' || String(item.i) !== fallback.i) {
    return { ...fallback };
  }
  return {
    ...fallback,
    x: clamp(item.x, 0, 100, fallback.x),
    y: clamp(item.y, 0, 10000, fallback.y),
    w: clamp(item.w, 1, 12, fallback.w),
    h: clamp(item.h, 2, 40, fallback.h),
  };
};

const normalizeLayout = (layout, defaults) => {
  const source = layout && typeof layout === 'object' ? layout : {};
  const normalizeBreakpoint = (name) => defaults[name].map((fallback) => {
    const candidate = Array.isArray(source[name])
      ? source[name].find((item) => String(item?.i) === fallback.i)
      : null;
    return normalizeLayoutItem(candidate, fallback);
  });
  return { lg: normalizeBreakpoint('lg'), xs: normalizeBreakpoint('xs') };
};

const normalizeWidgets = (widgets, defaults) => {
  const source = Array.isArray(widgets) ? widgets.map(String) : defaults;
  const unique = [];
  source.forEach((id) => {
    if (LIVE_STUDIO_WIDGET_IDS.includes(id) && !unique.includes(id)) unique.push(id);
  });
  // Always keep core broadcast widgets; side can be hidden.
  ['participants', 'mixer', 'playout'].forEach((id) => {
    if (!unique.includes(id)) unique.push(id);
  });
  return unique;
};

export const normalizeLiveStudioSettings = (value) => {
  const defaults = createDefaultLiveStudioSettings();
  if (!value || typeof value !== 'object' || value.version !== LIVE_STUDIO_SETTINGS_VERSION) {
    return defaults;
  }

  return {
    version: LIVE_STUDIO_SETTINGS_VERSION,
    stationId:
      typeof value.stationId === 'string' || typeof value.stationId === 'number'
        ? value.stationId
        : '',
    devices: {
      audioInputId: normalizeDeviceId(value.devices?.audioInputId),
      audioOutputId: normalizeDeviceId(value.devices?.audioOutputId),
      videoInputId: normalizeDeviceId(value.devices?.videoInputId),
    },
    defaultFaders: {
      program: clamp(value.defaultFaders?.program, 0, 1, defaults.defaultFaders.program),
      host: clamp(value.defaultFaders?.host, 0, 1, defaults.defaultFaders.host),
      guest: clamp(value.defaultFaders?.guest, 0, 1, defaults.defaultFaders.guest),
    },
    widgets: normalizeWidgets(value.widgets, defaults.widgets),
    layout: normalizeLayout(value.layout, defaults.layout),
  };
};

const browserStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const clearLegacySettings = (storage) => {
  LEGACY_SETTINGS_KEYS.forEach((key) => {
    try {
      storage.removeItem(key);
    } catch {
      // ignore
    }
  });
};

const migrateLegacySettings = (storage) => {
  for (const key of LEGACY_SETTINGS_KEYS) {
    let raw;
    try {
      raw = storage.getItem(key);
    } catch {
      return null;
    }
    if (!raw) continue;

    try {
      const legacy = JSON.parse(raw);
      if (!legacy || typeof legacy !== 'object') continue;
      const defaults = createDefaultLiveStudioSettings();
      const migrated = normalizeLiveStudioSettings({
        ...defaults,
        version: LIVE_STUDIO_SETTINGS_VERSION,
        stationId: legacy.stationId,
        devices: { ...defaults.devices, ...(legacy.devices || {}) },
        defaultFaders: { ...defaults.defaultFaders, ...(legacy.defaultFaders || {}) },
        widgets: legacy.widgets ?? defaults.widgets,
        layout: legacy.layout ?? defaults.layout,
      });
      storage.setItem(LIVE_STUDIO_SETTINGS_KEY, JSON.stringify(migrated));
      clearLegacySettings(storage);
      return migrated;
    } catch {
      try {
        storage.removeItem(key);
      } catch {
        // Keep looking for another usable legacy document.
      }
    }
  }
  clearLegacySettings(storage);
  return null;
};

export const loadLiveStudioSettings = (storage = browserStorage()) => {
  const defaults = createDefaultLiveStudioSettings();
  if (!storage) return defaults;

  try {
    const raw = storage.getItem(LIVE_STUDIO_SETTINGS_KEY);
    if (!raw) {
      return migrateLegacySettings(storage) || defaults;
    }
    const parsed = JSON.parse(raw);
    if (parsed?.version !== LIVE_STUDIO_SETTINGS_VERSION) {
      storage.removeItem(LIVE_STUDIO_SETTINGS_KEY);
      clearLegacySettings(storage);
      return defaults;
    }
    return normalizeLiveStudioSettings(parsed);
  } catch {
    try {
      storage.removeItem(LIVE_STUDIO_SETTINGS_KEY);
    } catch {
      // Storage may be disabled; defaults are still safe to use.
    }
    return defaults;
  }
};

export const saveLiveStudioSettings = (patch, storage = browserStorage()) => {
  const current = loadLiveStudioSettings(storage);
  const next = normalizeLiveStudioSettings({
    ...current,
    ...patch,
    version: LIVE_STUDIO_SETTINGS_VERSION,
    devices: { ...current.devices, ...(patch?.devices || {}) },
    defaultFaders: { ...current.defaultFaders, ...(patch?.defaultFaders || {}) },
    widgets: patch?.widgets !== undefined ? patch.widgets : current.widgets,
    layout: patch?.layout !== undefined ? patch.layout : current.layout,
  });

  if (storage) {
    try {
      storage.setItem(LIVE_STUDIO_SETTINGS_KEY, JSON.stringify(next));
    } catch {
      // Private browsing/quota failures must not break the studio.
    }
  }
  return next;
};

export const resetLiveStudioSettings = (storage = browserStorage()) => {
  if (storage) {
    try {
      storage.removeItem(LIVE_STUDIO_SETTINGS_KEY);
      clearLegacySettings(storage);
    } catch {
      // Return defaults even when storage cannot be modified.
    }
  }
  return createDefaultLiveStudioSettings();
};
